import { faChevronRight, faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    createStyles,
    Drawer,
    makeStyles,
    Slider,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import Button from 'components/UI/V2/Button/Button';
import { RadioGroup } from 'components/UI/V2/RadioGroup/RadioGroup';
import { useMarketplace } from 'hooks';
import React from 'react';
import { AddressAutocomplete } from './AddressAutocomplete';
import { SelectedPlace } from 'hooks/usePlacesAutocomplete';
import { SESSION_AREA_LABELS, SessionBusinessArea, SessionZone } from 'model/ServiceSession';
import { ZoneMap, ZoneMapsProvider } from './ZoneMap';
import { DEFAULT_RADIUS_MILES, MAX_RADIUS_MILES, MIN_RADIUS_MILES } from './zoneMapConfig';

export interface ZoneSelectionPanelProps {
    area: SessionBusinessArea;
    open: boolean;
    zone?: SessionZone;
    onSave: (zone: SessionZone) => void;
    onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawer: {
            '& .MuiDrawer-paperAnchorRight': {
                width: '100vw',
                boxShadow: 'none',

                [theme.breakpoints.up('md')]: {
                    width: 568,
                    right: 590
                }
            }
        },
        root: {
            'gap': 16,
            'height': '100%',
            'display': 'flex',
            'padding': '24px 24px',
            'boxSizing': 'border-box',
            'flexDirection': 'column',

            '&[data-type="map"]': {
                overflow: 'hidden'
            },

            '&[data-type="zip_codes"]': {
                overflow: 'auto'
            }
        },
        header: {
            display: 'flex',
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        title: {
            fontSize: 22,
            fontWeight: 600
        },
        headerActions: {
            gap: 10,
            display: 'flex',
            alignItems: 'center'
        },
        saveButton: {
            '&&': {
                width: 100,
                height: 39,
                boxSizing: 'border-box'
            }
        },
        collapseButton: {
            width: 39,
            height: 39,
            border: 0,
            display: 'flex',
            cursor: 'pointer',
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F1F1F1'
        },
        radioGroup: {
            gap: 24,
            display: 'flex',
            flexShrink: 0,
            flexDirection: 'row'
        },
        mapSection: {
            gap: 16,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
        },
        description: {
            fontSize: 15,
            lineHeight: 1.4,
            flexShrink: 0
        },
        radius: {
            gap: 8,
            display: 'flex',
            flexShrink: 0,
            flexDirection: 'column'
        },
        radiusHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        slider: {
            'color': '#5B8A72',
            'padding': '8px 0',

            '& .MuiSlider-rail': {
                height: 4,
                borderRadius: 2,
                backgroundColor: '#E5E5E5'
            },
            '& .MuiSlider-track': {
                height: 4,
                borderRadius: 2,
                backgroundColor: '#5B8A72'
            },
            '& .MuiSlider-thumb': {
                'width': 18,
                'height': 18,
                'marginTop': -7,
                'backgroundColor': '#5B8A72',

                '&:hover, &.MuiSlider-active': {
                    boxShadow: '0 0 0 8px rgba(91, 138, 114, 0.16)'
                }
            },
            '&.Mui-disabled': {
                'color': '#C4C4C4',

                '& .MuiSlider-thumb': {
                    backgroundColor: '#C4C4C4'
                },
                '& .MuiSlider-track': {
                    backgroundColor: '#C4C4C4'
                }
            }
        },
        zipCodeError: {
            fontSize: 13,
            color: '#D92C2C'
        },
        zipCodesSection: {
            flexShrink: 0,
            position: 'relative'
        },
        zipCodes: {
            gap: 12,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center'
        },
        zipCode: {
            gap: 7,
            height: 38,
            fontSize: 14,
            fontWeight: 500,
            display: 'flex',
            padding: '0 16px',
            borderRadius: 1000,
            alignItems: 'center',
            boxSizing: 'border-box',
            backgroundColor: '#F1F1F1'
        },
        removeZipCode: {
            border: 0,
            padding: 0,
            display: 'flex',
            cursor: 'pointer',
            appearance: 'none',
            background: 'none'
        },
        addZipCode: {
            width: 38,
            height: 38,
            fontSize: 20,
            display: 'flex',
            cursor: 'pointer',
            borderRadius: '50%',
            alignItems: 'center',
            background: 'transparent',
            justifyContent: 'center',
            border: '1px solid #000000'
        },
        zipCodePopover: {
            gap: 8,
            zIndex: 1,
            display: 'flex',
            position: 'absolute',
            flexDirection: 'column'
        },
        zipCodeForm: {
            width: 155,
            height: 78,
            display: 'flex',
            overflow: 'hidden',
            borderRadius: 14,
            flexDirection: 'column',
            boxSizing: 'border-box',
            backgroundColor: '#FFFFFF',
            border: '1px solid #000000'
        },
        zipCodeInput: {
            flex: 1,
            border: 0,
            fontSize: 14,
            outline: 'none',
            padding: '0 12px',
            fontFamily: 'inherit'
        },
        zipCodeSubmit: {
            gap: 6,
            height: 36,
            border: 0,
            fontSize: 16,
            fontWeight: 500,
            display: 'flex',
            flexShrink: 0,
            cursor: 'pointer',
            color: '#FFFFFF',
            fontFamily: 'inherit',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#92B4A7'
        },
        label: {
            fontSize: 16,
            fontWeight: 600
        }
    })
);

const ZONE_OPTIONS = [
    { value: 'map' as const, inlineLabel: 'Select From Map' },
    { value: 'zip_codes' as const, inlineLabel: 'By Zip Code' }
];

const sanitizeZipCode = (value: string, isCanada: boolean): string =>
    isCanada
        ? value
              .replace(/[^a-zA-Z0-9]/g, '')
              .toUpperCase()
              .slice(0, 6)
        : value.replace(/\D/g, '').slice(0, 5);

export const ZoneSelectionPanel: React.FC<ZoneSelectionPanelProps> = props => {
    const classes = useStyles();
    const theme = useTheme();
    const marketplace = useMarketplace();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isCanada = marketplace.basics.address.country?.id.toUpperCase() === 'CA';
    const areaLabel = SESSION_AREA_LABELS[props.area].toLowerCase();

    const [type, setType] = React.useState<SessionZone['type']>('map');
    const [radius, setRadius] = React.useState<number>(DEFAULT_RADIUS_MILES);
    const [latitude, setLatitude] = React.useState<number>();
    const [longitude, setLongitude] = React.useState<number>();
    const [addressQuery, setAddressQuery] = React.useState('');
    const [needsGeocode, setNeedsGeocode] = React.useState(false);
    const [zipCodes, setZipCodes] = React.useState<Array<string>>([]);
    const [zipCode, setZipCode] = React.useState('');
    const [zipCodeError, setZipCodeError] = React.useState<string>();
    const [zipFormOpen, setZipFormOpen] = React.useState(false);
    const [zipFormOffset, setZipFormOffset] = React.useState({ left: 0, top: 0 });
    const addZipCodeRef = React.useRef<HTMLButtonElement>(null);
    const zipPopoverRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!props.open) return;

        setType(props.zone?.type || 'map');
        setRadius(props.zone?.type === 'map' ? props.zone.radius : DEFAULT_RADIUS_MILES);
        setZipCodes(props.zone?.type === 'zip_codes' ? props.zone.zipCodes : []);
        setZipCode('');
        setZipCodeError(undefined);
        setZipFormOpen(false);

        if (props.zone?.type === 'map' && props.zone.latitude !== undefined && props.zone.longitude !== undefined) {
            setLatitude(props.zone.latitude);
            setLongitude(props.zone.longitude);
            setAddressQuery(props.zone.address || '');
            setNeedsGeocode(!props.zone.address);
            return;
        }

        setLatitude(undefined);
        setLongitude(undefined);
        setAddressQuery('');
        setNeedsGeocode(false);
    }, [props.open, props.zone]);

    const closeZipFormHandler = React.useCallback(() => {
        setZipFormOpen(false);
        setZipCodeError(undefined);
    }, []);

    React.useEffect(() => {
        if (!zipFormOpen) return;

        const clickHandler = (event: MouseEvent) => {
            const target = event.target as Node;
            if (zipPopoverRef.current?.contains(target)) return;
            if (addZipCodeRef.current?.contains(target)) return;

            closeZipFormHandler();
        };

        document.addEventListener('mousedown', clickHandler);
        return () => document.removeEventListener('mousedown', clickHandler);
    }, [zipFormOpen, closeZipFormHandler]);

    const openZipFormHandler = () => {
        const button = addZipCodeRef.current;
        if (!button) return;

        setZipFormOffset({ left: button.offsetLeft, top: button.offsetTop + button.offsetHeight + 18 });
        setZipCode('');
        setZipCodeError(undefined);
        setZipFormOpen(true);
    };

    const hasLocation = latitude !== undefined && longitude !== undefined;
    const isValid = type === 'map' ? hasLocation : zipCodes.length > 0;

    const addZipCodeHandler = () => {
        const value = zipCode.trim();

        if (!value) return;

        if (zipCodes.includes(value)) {
            setZipCodeError('This ZIP code is already on the list.');
            return;
        }

        setZipCodes(prev => [...prev, value]);
        setZipCode('');
        setZipCodeError(undefined);
        setZipFormOpen(false);
    };

    const changeAddressHandler = (value: string) => {
        setAddressQuery(value);
        setNeedsGeocode(false);

        if (value.trim()) return;

        setLatitude(undefined);
        setLongitude(undefined);
    };

    const selectPlaceHandler = (place: SelectedPlace) => {
        setAddressQuery(place.address);
        setLatitude(place.latitude);
        setLongitude(place.longitude);
        setNeedsGeocode(false);
    };

    const resolvedAddressHandler = React.useCallback((address: string) => {
        setAddressQuery(current => current || address);
        setNeedsGeocode(false);
    }, []);

    const saveHandler = () => {
        if (type === 'map') {
            if (latitude === undefined || longitude === undefined) return;

            props.onSave({
                type: 'map',
                radius,
                latitude,
                longitude,
                address: addressQuery.trim() || undefined
            });
            return;
        }

        props.onSave({ type: 'zip_codes', zipCodes });
    };

    return (
        <Drawer
            anchor="right"
            open={props.open}
            onClose={props.onClose}
            className={classes.drawer}
            // MUI sets the modal z-index inline: below the slot drawer paper, above the dimmed page.
            style={{ zIndex: isFullScreen ? 1400 : 1200 }}
        >
            <div
                className={classes.root}
                data-type={type}
            >
                <div className={classes.header}>
                    <Typography className={classes.title}>Zone Selection</Typography>

                    <div className={classes.headerActions}>
                        <Button
                            variant="green"
                            label="Save"
                            disabled={!isValid}
                            noColorOnDisabled
                            className={classes.saveButton}
                            id={`${props.area}-zone-save-button`}
                            onClick={saveHandler}
                        />

                        <button
                            type="button"
                            className={classes.collapseButton}
                            id={`${props.area}-zone-collapse-button`}
                            onClick={props.onClose}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>

                <RadioGroup<SessionZone['type']>
                    value={type}
                    options={ZONE_OPTIONS}
                    className={classes.radioGroup}
                    onChange={setType}
                />

                {type === 'map' && props.open && (
                    <ZoneMapsProvider>
                        <div className={classes.mapSection}>
                            <Typography className={classes.description}>
                                Search for an address to set the service zone where each staff member will provide{' '}
                                {areaLabel} services.
                            </Typography>

                            <AddressAutocomplete
                                area={props.area}
                                value={addressQuery}
                                onChange={changeAddressHandler}
                                onSelect={selectPlaceHandler}
                            />

                            <ZoneMap
                                area={props.area}
                                latitude={latitude}
                                longitude={longitude}
                                radiusMiles={radius}
                                interactive={hasLocation}
                                resolveAddress={needsGeocode}
                                onResolvedAddress={resolvedAddressHandler}
                            />

                            <div className={classes.radius}>
                                <div className={classes.radiusHeader}>
                                    <Typography className={classes.label}>Radius (mi)</Typography>
                                    <Typography className={classes.label}>{radius} mi</Typography>
                                </div>
                                <Slider
                                    min={MIN_RADIUS_MILES}
                                    max={MAX_RADIUS_MILES}
                                    step={1}
                                    value={radius}
                                    disabled={!hasLocation}
                                    className={classes.slider}
                                    id={`${props.area}-zone-radius-slider`}
                                    aria-label="Radius in miles"
                                    onChange={(_event, value) =>
                                        setRadius(typeof value === 'number' ? value : value[0])
                                    }
                                />
                            </div>
                        </div>
                    </ZoneMapsProvider>
                )}

                {type === 'zip_codes' && (
                    <>
                        <Typography className={classes.description}>
                            Enter zip codes to quickly define service zones for your {areaLabel} staff coverage areas.
                        </Typography>

                        <div className={classes.zipCodesSection}>
                            <div className={classes.zipCodes}>
                                {zipCodes.map(code => (
                                    <span
                                        key={code}
                                        className={classes.zipCode}
                                    >
                                        {code}
                                        <button
                                            type="button"
                                            className={classes.removeZipCode}
                                            onClick={() => setZipCodes(prev => prev.filter(item => item !== code))}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </span>
                                ))}

                                <button
                                    type="button"
                                    ref={addZipCodeRef}
                                    className={classes.addZipCode}
                                    id={`${props.area}-zone-add-zipcode-button`}
                                    aria-label="Add zip code"
                                    onClick={() => (zipFormOpen ? closeZipFormHandler() : openZipFormHandler())}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </div>

                            {zipFormOpen && (
                                <div
                                    ref={zipPopoverRef}
                                    style={zipFormOffset}
                                    className={classes.zipCodePopover}
                                >
                                    <div className={classes.zipCodeForm}>
                                        <input
                                            autoFocus
                                            type="text"
                                            value={zipCode}
                                            autoComplete="off"
                                            id={`${props.area}-zone-zipcode-input`}
                                            className={classes.zipCodeInput}
                                            onChange={event => {
                                                setZipCode(sanitizeZipCode(event.target.value, isCanada));
                                                setZipCodeError(undefined);
                                            }}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter') addZipCodeHandler();
                                                if (event.key === 'Escape') closeZipFormHandler();
                                            }}
                                        />

                                        <button
                                            type="button"
                                            className={classes.zipCodeSubmit}
                                            id={`${props.area}-zone-submit-zipcode-button`}
                                            onClick={addZipCodeHandler}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            Add Zip Code
                                        </button>
                                    </div>

                                    {zipCodeError && (
                                        <Typography className={classes.zipCodeError}>{zipCodeError}</Typography>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </Drawer>
    );
};
